/*
 *  @providesModule InsufficientFundsModal
 *
 */
import React from 'react';

import {_m} from 'INTL';
import AWSManager from 'AWSManager';
import Background from 'Background';
import CheckoutController from 'CheckoutController';
import ClientPropertyConstants from 'ClientPropertyConstants';
import DepositOffersGrid from 'DepositOffersGrid';
import GlobalEventEmitter from 'GlobalEventEmitter';
import HeaderModal from 'HeaderModal';
import Label from 'Label';
import LTOInfo from 'LTOInfo';
import LTOModal from 'LTOModal';
import LTOUtils from 'LTOUtils';
import ModalNavigationController from 'ModalNavigationController';
import NavigationManager from 'NavigationManager';
import PropTypes from 'prop-types';
import PushButton from 'PushButton';
import SkillzStyle from 'SkillzStyle';
import SkillzSVG from 'SkillzSVG';
import SKZConstants from 'SKZConstants';
import StoreFrontStore from 'StoreFrontStore';
import {StoreInfoV1} from 'StoreInfo';
import style, {DEPOSIT_TILE_HEIGHT} from 'InsufficientFundsModalStyle';

import {ActivityIndicator, LayoutChangeEvent, StyleSheet, View} from 'react-native';
import SKZLayoutAnimations from 'SKZLayoutAnimations';

const {
  btnGradientOne,
  btnGradientTwo,
  tabHighlightColor,
  textColorPrimary,
  viewCornerRadiusSecondary,
} = SkillzStyle.fullStyles;

const MAX_DEPOSIT_GRID_ROWS = 3;

type Props = {
  isCash: boolean;
  completion?: () => void;
};

type State = {
  isLoading: boolean;
  offerInfo: LTOInfo | null;
  storeInfo: StoreInfoV1 | null;
  offersBoxWidth: number | null;
  offersBoxHeight: number | null;
};

export default class InsufficientFundsModal extends React.Component<Props, State> {
  static propTypes = {
    isCash: PropTypes.bool,
    completion: PropTypes.func,
  };

  static defaultProps = {
    completion: () => {},
  };

  constructor(props: any) {
    super(props);
    this.state = {
      isLoading: true,
      offerInfo: null,
      storeInfo: null,
      offersBoxWidth: null,
      offersBoxHeight: 0,
    };
  }

  componentDidMount() {
    this._fetchDataForInsufficientFundsDisplay((hasLTO: boolean) => {
      this._trackModalDisplay(hasLTO);
      this._setOffersBoxHeight();
      SKZLayoutAnimations.easeInEaseOut();
      this.setState({
        isLoading: false,
      });
    });
  }

  //Set height of the modal content for Deposit Grid
  _setOffersBoxHeight() {
    if (this.state.storeInfo != null) {
      const offers = this.state.storeInfo.offers;
      const length = offers?.length || 0;
      const rows = Math.ceil(length / 2);
      const gridHeight =
        DEPOSIT_TILE_HEIGHT * (rows > MAX_DEPOSIT_GRID_ROWS ? MAX_DEPOSIT_GRID_ROWS : rows);
      this.setState({
        offersBoxHeight: gridHeight,
      });
    }
  }

  _trackModalDisplay(hasLTO: boolean) {
    if (hasLTO) {
      // Either the HISTORICAL LTO modal or the NEW Add Funds LTO Modal appears
      AWSManager.trackEvent('ADD_FUNDS_MODAL', {
        result: ClientPropertyConstants.getAddFundsLTOModalType(),
      });
    } else {
      // The "Out of Funds" modal appears
      AWSManager.trackEvent('OUT_OF_FUNDS_MODAL');
    }
  }

  _fetchDataForInsufficientFundsDisplay(completion: (hasLTO: boolean) => void): LTOInfo {
    StoreFrontStore.fetchDepositOffersWithCompletion(false, (storeInfo: any) => {
      const offerInfo: LTOInfo = LTOUtils._determineOfferToDisplay(storeInfo, this.props.isCash);
      if (offerInfo && !offerInfo.end) {
        LTOUtils.triggerLTO(offerInfo)
          .then((ltoInfo: LTOInfo) => {
            this.setState({
              offerInfo: ltoInfo,
              storeInfo: storeInfo,
            });
            completion(true);
            GlobalEventEmitter.emit('RefreshStore');
          })
          .catch(() => {
            this.setState({storeInfo});
            completion(true);
          });
      } else {
        this.setState(
          {
            offerInfo: offerInfo,
            storeInfo: storeInfo,
          },
          () => completion(offerInfo)
        );
      }
    });
  }

  _hideModal = () => {
    // This completion is passed in from SyncMatchMakingController
    if (this.props.completion) {
      this.props.completion();
    }
    ModalNavigationController.hideModal();
  };

  _onClaimOffer = () => {
    AWSManager.trackEvent('ADD_FUNDS_MODAL_NEXT', {
      result: ClientPropertyConstants.getAddFundsLTOModalType(),
    });
    this._hideModal();
    setTimeout(() => {
      CheckoutController.launchCheckoutFlow({
        storeInfo: this.state.storeInfo!,
        offer: this.state.offerInfo,
        navigator: NavigationManager.getNavigator(),
      });
    }, SKZConstants.SHORT_DELAY_MS);
  };

  switchToStoreTab() {
    this._hideModal();
    const navigator = NavigationManager.getNavigator();
    navigator.popToTop();
    GlobalEventEmitter.emit('switchToTab', 'STORE');
  }

  _renderAddLTOModal(): React.ReactElement {
    const offerInfo = this.state.offerInfo;
    const modalType = ClientPropertyConstants.getAddFundsLTOModalType();

    return (
      <LTOModal
        modalStyle={modalType}
        onCancel={this._hideModal}
        onDone={this._onClaimOffer}
        offerInfo={offerInfo}
      />
    );
  }

  _outOfFundsModalNextPress() {
    AWSManager.trackEvent('OUT_OF_FUNDS_NEXT');
    if (this.props.completion) {
      this.props.completion();
    }
    this.switchToStoreTab();
  }

  _renderGoToStoreModal(): React.ReactElement {
    return (
      <HeaderModal title={_m('OUT OF FUNDS')}>
        <View style={{alignItems: 'center'}}>
          <SkillzSVG
            style={style.outOfFundsStoreIcon}
            source={'icon_footer_store.svg'}
            tintColor={tabHighlightColor.color}
          />
          <Label style={style.textOutOfFunds}>{_m('Go to the store to get more!')}</Label>
          <View style={style.buttonArrayOutOfFunds}>
            <PushButton
              style={style.buttonOutOfFunds}
              title={_m('BACK')}
              textStyle={style.backButtonText}
              cornerRadius={viewCornerRadiusSecondary.borderRadius}
              colors={btnGradientTwo}
              onPress={this._hideModal}
            />
            <PushButton
              style={style.buttonOutOfFunds}
              title={_m('NEXT')}
              textStyle={style.okButtonText}
              cornerRadius={viewCornerRadiusSecondary.borderRadius}
              colors={btnGradientOne}
              onPress={() => {
                this._outOfFundsModalNextPress();
              }}
            />
          </View>
        </View>
      </HeaderModal>
    );
  }

  _didLayoutDepositOffers = (e: LayoutChangeEvent) => {
    this.setState({
      offersBoxWidth: e.nativeEvent.layout.width,
    });
  };

  _renderAddFundsModal(): React.ReactElement {
    return (
      <HeaderModal title={_m('OUT OF FUNDS')}>
        <View style={style.outerStoreContainer}>
          <View style={style.labelContainer}>
            <Label style={style.textDepositToContinue}>{_m('DEPOSIT TO CONTINUE:')}</Label>
          </View>
          <View style={[style.innerStoreContainer, {height: this.state.offersBoxHeight}]}>
            <DepositOffersGrid
              offersBoxWidth={this.state.offersBoxWidth}
              onLayout={this._didLayoutDepositOffers}
              loaded={true}
              mainNavigator={NavigationManager.getNavigator()}
              storeResponse={this.state.storeInfo}
              onSelectOffer={this._hideModal}
              maxOffersToDisplay={6}
              style={{height: 100}}
            />
          </View>
          <PushButton
            onPress={this._hideModal}
            style={style.buttonClose}
            textStyle={style.backButtonText}
            colors={btnGradientOne}
            title={_m('CLOSE')}
            cornerRadius={viewCornerRadiusSecondary.borderRadius}
            accessibilityLabel={'depositFunds_modal_cancel'}
          />
        </View>
      </HeaderModal>
    );
  }

  _renderOutOfFundsModal(): React.ReactElement {
    if (
      ClientPropertyConstants.getInsuffcientFundsDepositEnabled() &&
      this.props.isCash &&
      this.state.storeInfo != null
    ) {
      return this._renderAddFundsModal();
    } else {
      return this._renderGoToStoreModal();
    }
  }

  renderInsufficientFundsModal(): React.ReactElement {
    // Check if we successfully fetched an LTO
    if (this.state.offerInfo) {
      return this._renderAddLTOModal();
    } else {
      return this._renderOutOfFundsModal();
    }
  }

  render(): React.ReactElement {
    // Show an activity indicator while we determine whether or not the user has an LTO
    if (this.state.isLoading) {
      return (
        <View style={[StyleSheet.absoluteFill, {justifyContent: 'center', alignItems: 'center'}]}>
          <Background style={style.backgroundImage}>
            <View style={[style.activityBackground, StyleSheet.absoluteFill]}>
              <View style={style.activityIndicatorContainer}>
                <ActivityIndicator
                  style={style.activityIndicator}
                  color={textColorPrimary.color}
                  size={'small'}
                />
              </View>
            </View>
          </Background>
        </View>
      );
    } else {
      return this.renderInsufficientFundsModal();
    }
  }
}
